// src/components/WhitelistForm/WhitelistForm.jsx
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { FaDiscord, FaClipboardList, FaCheckCircle, FaClock, FaTimesCircle } from 'react-icons/fa';
import './WhitelistForm.css';

const WhitelistForm = () => {
  const { isLoggedIn, user, authToken } = useAuth();
  const navigate = useNavigate();

  const [nombreUsuario, setNombreUsuario] = useState('');
  const [edad, setEdad] = useState('');
  const [experienciaRol, setExperienciaRol] = useState('');
  const [historiaPj, setHistoriaPj] = useState('');
  const [objetivosPj, setObjetivosPj] = useState('');
  const [aptitudesPj, setAptitudesPj] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Estados para verificar si ya tiene whitelist
  const [whitelistStatus, setWhitelistStatus] = useState(null);
  const [whitelistId, setWhitelistId] = useState(null);
  const [checkingWhitelist, setCheckingWhitelist] = useState(true);

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://riasbaixaspd.es/api';

  useEffect(() => {
    if (!isLoggedIn || !user) {
      setCheckingWhitelist(false);
      return;
    }

    const checkMyWhitelist = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/whitelistRequests/me`, {
          headers: {
            'Authorization': `Bearer ${authToken}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          if (data.found) {
            setWhitelistStatus(data.request.status);
            setWhitelistId(data.request.id);
          } else {
            setWhitelistStatus(null);
            setWhitelistId(null);
          }
        }
      } catch (err) {
        console.error('Error al comprobar whitelist:', err);
      } finally {
        setCheckingWhitelist(false);
      }
    };

    checkMyWhitelist();
  }, [isLoggedIn, user, authToken, API_BASE_URL]);

  const handleDiscordLoginClick = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/discord/url`);
      if (!response.ok) {
        throw new Error('No se pudo obtener la URL de autorización de Discord.');
      }
      const data = await response.json();
      window.location.href = data.url;
    } catch (err) {
      setMessage('Error al conectar con Discord: ' + err.message);
    }
  };

  const handleRetry = async () => {
    if (!whitelistId) return;
    setCheckingWhitelist(true);
    try {
      const response = await fetch(`${API_BASE_URL}/whitelistRequests/${whitelistId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });
      if (response.ok) {
        setWhitelistStatus(null);
        setWhitelistId(null);
        setMessage('');
      } else {
        const errData = await response.json();
        setMessage(`Error al reiniciar: ${errData.message || 'Inténtalo de nuevo.'}`);
      }
    } catch (err) {
      setMessage('Error al intentar reiniciar.');
    } finally {
      setCheckingWhitelist(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setSubmitting(true);

    const newRequest = {
      username: nombreUsuario.trim(),
      ooc_edad: edad.trim(),
      ooc_experiencia_rol: experienciaRol.trim(),
      ic_historia_pj: historiaPj.trim(),
      ic_futuro_pj: objetivosPj.trim(),
      ic_aptitudes_pj: aptitudesPj.trim()
    };

    if (!nombreUsuario || !edad || !experienciaRol || !historiaPj || !objetivosPj || !aptitudesPj) {
      setMessage('Error: Por favor, completa todos los campos requeridos.');
      setSubmitting(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/whitelistRequests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify(newRequest),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al enviar la solicitud.');
      }

      setMessage('¡Solicitud de Whitelist enviada con éxito!');
      setWhitelistStatus('pending');
      
      // Intentar obtener el ID recién creado
      const getNewMe = await fetch(`${API_BASE_URL}/whitelistRequests/me`, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });
      if (getNewMe.ok) {
        const data = await getNewMe.json();
        if (data.found) {
          setWhitelistId(data.request.id);
        }
      }

      // Limpiar campos
      setNombreUsuario('');
      setEdad('');
      setExperienciaRol('');
      setHistoriaPj('');
      setObjetivosPj('');
      setAptitudesPj('');
    } catch (err) {
      console.error('Error al enviar la solicitud:', err);
      setMessage(`Error al enviar la solicitud: ${err.message || 'Inténtalo de nuevo más tarde.'}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (checkingWhitelist) {
    return (
      <div className="whitelist-form-container glass-panel status-card-container" style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>
        <div className="modern-spinner"></div>
      </div>
    );
  }

  // Flujo 1: Usuario no autenticado
  if (!isLoggedIn || !user) {
    return (
      <div className="whitelist-form-container glass-panel status-card-container" style={{ textAlign: 'center', padding: '40px' }}>
        <h2>Formulario de Whitelist</h2>
        <p style={{ color: '#bbb', marginBottom: '30px', fontSize: '1.05rem', lineHeight: '1.6' }}>
          Para poder enviar una solicitud de Whitelist en nuestro servidor de FiveM, debes iniciar sesión con tu cuenta de Discord primero.
        </p>
        <button type="button" onClick={handleDiscordLoginClick} className="discord-login-button" style={{ maxWidth: '320px', margin: '0 auto' }}>
          <FaDiscord className="discord-icon" />
          <span>Entrar con Discord</span>
        </button>
      </div>
    );
  }

  // Flujo 2: El usuario ya tiene una whitelist registrada
  if (whitelistStatus === 'approved') {
    return (
      <div className="whitelist-form-container glass-panel status-card-container" style={{ textAlign: 'center', padding: '40px' }}>
        <FaCheckCircle style={{ fontSize: '4rem', color: '#00e676', marginBottom: 20 }} />
        <h2>¡Tu Whitelist ya está Aprobada!</h2>
        <p style={{ color: '#bbb', marginBottom: '30px', fontSize: '1.05rem' }}>
          Ya formas parte de la comunidad. No es necesario enviar otra solicitud.
        </p>
        <div style={{ display: 'flex', gap: 15, justifyContent: 'center' }}>
          <Link to="/dashboard" className="dashboard-action-btn primary">
            <span>Ir al Panel de Usuario</span>
          </Link>
          <Link to="/como-conectarse" className="dashboard-action-btn secondary">
            <span>Guía de Conexión</span>
          </Link>
        </div>
      </div>
    );
  }

  if (whitelistStatus === 'pending') {
    return (
      <div className="whitelist-form-container glass-panel status-card-container" style={{ textAlign: 'center', padding: '40px' }}>
        <FaClock style={{ fontSize: '4rem', color: '#ff9100', marginBottom: 20 }} />
        <h2>Solicitud Pendiente de Revisión</h2>
        <p style={{ color: '#bbb', marginBottom: '30px', fontSize: '1.05rem', lineHeight: '1.6' }}>
          Ya has enviado una solicitud de Whitelist. Actualmente está siendo revisada por el Staff de nuestro servidor.
        </p>
        <Link to="/dashboard" className="dashboard-action-btn primary" style={{ margin: '0 auto', display: 'inline-flex' }}>
          <span>Ver estado en mi Panel</span>
        </Link>
      </div>
    );
  }

  if (whitelistStatus === 'rejected') {
    return (
      <div className="whitelist-form-container glass-panel status-card-container" style={{ textAlign: 'center', padding: '40px' }}>
        <FaTimesCircle style={{ fontSize: '4rem', color: '#ff1744', marginBottom: 20 }} />
        <h2>Whitelist Suspendida / Rechazada</h2>
        <p style={{ color: '#bbb', marginBottom: '30px', fontSize: '1.05rem', lineHeight: '1.6' }}>
          Tu solicitud de whitelist ha sido rechazada o suspendida por el Staff. Puedes volver a intentarlo enviando una nueva solicitud.
        </p>
        {message && <p className="message error-message" style={{ marginBottom: '20px' }}>{message}</p>}
        <div style={{ display: 'flex', gap: 15, justifyContent: 'center' }}>
          <button onClick={handleRetry} className="dashboard-action-btn primary" style={{ cursor: 'pointer', border: 'none' }}>
            <span>Volver a Intentarlo</span>
          </button>
          <Link to="/dashboard" className="dashboard-action-btn secondary">
            <span>Ir al Panel de Usuario</span>
          </Link>
        </div>
      </div>
    );
  }

  // Flujo 3: Mostrar Formulario Normal
  return (
    <div className="whitelist-form-container glass-panel">
      <h2>Formulario de Whitelist</h2>
      <p style={{ color: '#bbb', marginBottom: '25px' }}>
        Completa las siguientes preguntas para aplicar a la whitelist de nuestro servidor de FiveM.
      </p>

      {/* Info de Discord vinculada */}
      <div className="discord-linked-info" style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 18px',
        background: 'rgba(88, 101, 242, 0.1)',
        border: '1px solid rgba(88, 101, 242, 0.25)',
        borderRadius: 8,
        marginBottom: 30
      }}>
        <FaDiscord style={{ color: '#5865F2', fontSize: '1.4rem' }} />
        <span style={{ fontSize: '0.95rem' }}>Conectado como: <b>{user.username}</b> (ID: {user.discord_id || user.id})</span>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Preguntas OOC (Out-Of-Character) */}
        <fieldset>
          <legend>Información Fuera de Personaje (OOC)</legend>
          
          <div className="form-group">
            <label htmlFor="nombreUsuario">Nombre de Usuario (Tu nombre de FiveM):</label>
            <input
              type="text"
              id="nombreUsuario"
              value={nombreUsuario}
              onChange={(e) => setNombreUsuario(e.target.value)}
              placeholder="Ej: JuanPerez"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="edad">¿Cuántos años tienes?</label>
            <input
              type="number"
              id="edad"
              value={edad}
              onChange={(e) => setEdad(e.target.value)}
              placeholder="Ej: 21"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="experienciaRol">¿Tienes experiencia en Roleplay? Describe brevemente:</label>
            <textarea
              id="experienciaRol"
              value={experienciaRol}
              onChange={(e) => setExperienciaRol(e.target.value)}
              placeholder="Cuéntanos en qué otros servidores has roleado..."
              rows="3"
              required
            ></textarea>
          </div>
        </fieldset>

        {/* Preguntas IC (In-Character) */}
        <fieldset>
          <legend>Información Dentro de Personaje (IC)</legend>
          
          <div className="form-group">
            <label htmlFor="historiaPj">Describe la historia de tu personaje (backstory):</label>
            <textarea
              id="historiaPj"
              value={historiaPj}
              onChange={(e) => setHistoriaPj(e.target.value)}
              placeholder="Sé detallado. Escribe la infancia, motivaciones y cómo llegó a la ciudad..."
              rows="10"
              required
            ></textarea>
          </div>

          <div className="form-group">
            <label htmlFor="objetivosPj">¿Qué esperas que tu personaje logre o experimente en el servidor?</label>
            <textarea
              id="objetivosPj"
              value={objetivosPj}
              onChange={(e) => setObjetivosPj(e.target.value)}
              placeholder="Metas, aspiraciones y el rol que desea desempeñar..."
              rows="5"
              required
            ></textarea>
          </div>

          <div className="form-group">
            <label htmlFor="aptitudesPj">¿Cuáles son las aptitudes o habilidades más destacadas de tu personaje?</label>
            <textarea
              id="aptitudesPj"
              value={aptitudesPj}
              onChange={(e) => setAptitudesPj(e.target.value)}
              placeholder="Habilidades físicas, mentales, debilidades, etc."
              rows="3"
              required
            ></textarea>
          </div>
        </fieldset>

        <button type="submit" disabled={submitting} className="submit-button">
          {submitting ? 'Enviando...' : 'Enviar Solicitud'}
        </button>

        {message && <p className={`message ${message.startsWith('Error') ? 'error-message' : 'success-message'}`}>{message}</p>}
      </form>
    </div>
  );
};

export default WhitelistForm;